import { Box, Button, TextField, InputLabel, Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { CSVLink } from 'react-csv';
import { useMsal } from '@azure/msal-react';

interface UpdateTrainingStatusData {
  email: string;
  status?: string;
}

const UpdateTrainingStatus = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const queryClient = useQueryClient();
  const SESSION_TOKEN_KEY = 'secondSessionToken';

  const { accounts } = useMsal();
  const user = accounts[0];

  const updateUserManualyMutation = useAuthenticatedMutation(['postUserDetails', 'PostData'], (postData) => ({
    url: `${fixed}training/update-training-status`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postData,
  }));

  const loginUser = async (emailToUpdate: string): Promise<string | null> => {
    if (!user) {
      console.error('No user found in MSAL accounts');
      return null;
    }

    const loginPayload = {
      username: emailToUpdate,
      email: emailToUpdate,
    };

    try {
      const response = await fetch(`${fixed}proxy/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginPayload),
      });

      if (!response.ok) {
        throw new Error('Failed to login');
      }

      const data = await response.json();
      const token = data.data.access_token;
      console.log('Tokenasas:', token);
      sessionStorage.setItem(SESSION_TOKEN_KEY, token);
      console.log('Login successful, token stored in session storage');
      return token;
    } catch (error) {
      console.error('Error logging in:', error);
      return null;
    }
  };

  const ensureToken = async (emailToUpdate: string) => {
    try {
      const token = await loginUser(emailToUpdate);
      if (!token) {
        console.error('Failed to obtain fresh token for:', emailToUpdate);
        return null;
      }
      sessionStorage.setItem(SESSION_TOKEN_KEY, token);
      console.log('New token obtained for:', emailToUpdate);
      return token;
    } catch (error) {
      console.error('Error ensuring token:', error);
      return null;
    }
  };

  const extractErrorMessage = (text: string): string => {
    try {
      const parsed = JSON.parse(text);
      return parsed.message || 'Unknown error';
    } catch {
      return text;
    }
  };

  const getUser = async (email: string) => {
    if (!email || email.trim() === '') {
      console.error('Empty email provided to getUser');
      return null;
    }

    const token = await ensureToken(email);
    if (!token) {
      console.error('Failed to obtain session token');
      return null;
    }

    try {
      const response = await fetch(`${fixed}proxy/getUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          token,
        }),
      });

      const responseText = await response.text();

      try {
        const data = JSON.parse(responseText);

        if (data.message && data.message.includes('already updated')) {
          return {
            isAlreadyUpdated: true,
            username: null,
            message: data.message,
          };
        }

        if (data.message && data.message.includes('already updated')) {
          console.log('User already updated:', data.message);
          return null;
        }

        if (data.userlist && Array.isArray(data.userlist)) {
          const activeUser = data.userlist.find((user) => user?.statuskey === '1');
          if (activeUser) {
            return activeUser.username;
          }
        }

        const errorMessage = data.message || 'No active user found';
        throw new Error(errorMessage);
      } catch (parseError) {
        const errorMessage = extractErrorMessage(responseText);
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('GetUser Error:', {
        email,
        error: errorMessage,
      });

      if (!errorMessage.includes('already updated')) {
        toast.error(errorMessage);
      }
      return null;
    }
  };

  const updateUser = async (emailToUpdate: string) => {
    if (!emailToUpdate) {
      console.error('No email found');
      return false;
    }

    const username = await getUser(emailToUpdate);
    if (!username) {
      console.error('No username found');
      return false;
    }

    const token = await ensureToken(emailToUpdate);
    if (!token) {
      console.error('Failed to obtain session token');
      return false;
    }

    try {
      const response = await fetch(`${fixed}proxy/updateUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email: emailToUpdate, token }),
      });

      const result = await response.text();
      if (response.ok && result.includes('User Updated Successfully')) {
        toast.success('Activity updated successfully');
        return true;
      } else {
        console.error('Failed to update activity');
        return false;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      return false;
    }
  };

  const handleUpdateStatus = async (email: string) => {
    try {
      const userResult = await getUser(email);

      if (userResult?.isAlreadyUpdated) {
        toast.info(`User is already updated in Saviynt. Updating local database...`);
        await updateUserManualyMutation.mutateAsync({
          email,
          status: 'Completed',
        } as UpdateTrainingStatusData);

        setEmail('');

        await queryClient.invalidateQueries({
          queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
        });

        await queryClient.refetchQueries({
          queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
          type: 'active',
        });

        toast.success('Local database updated successfully');
        return;
      }

      const updateUserResult = await updateUser(email);

      if (!updateUserResult) {
        toast.error(`Failed to update user activity for ${email}`);
        return;
      }

      setEmail('');

      await queryClient.invalidateQueries({
        queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
      });

      await queryClient.refetchQueries({
        queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
        type: 'active',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('An error occurred while updating training status:', errorMessage);
      toast.error(`Failed to update training status: ${errorMessage}`);
    }
  };

  const handleBulkUpdate = async () => {
    try {
      const selectedEmails = filteredData
        .filter((item) => selectedIds.includes(item.id.toString()))
        .map((item) => item.email);

      if (selectedEmails.length === 0) {
        toast.error('Please select at least one email to update');
        return;
      }

      let successCount = 0;
      let alreadyUpdatedCount = 0;

      for (const email of selectedEmails) {
        const userResult = await getUser(email);

        if (userResult?.isAlreadyUpdated) {
          // Update local database for already updated users
          try {
            await updateUserManualyMutation.mutateAsync({
              email,
              status: 'Completed',
            } as UpdateTrainingStatusData);
            alreadyUpdatedCount++;
            continue;
          } catch (error) {
            toast.error(`Failed to update local database for ${email}`);
            continue;
          }
        }

        // Regular update flow for non-updated users
        const updateUserResult = await updateUser(email);
        if (!updateUserResult) {
          toast.error(`Failed to update user activity for ${email}`);
          continue;
        }
        successCount++;
      }

      const totalUpdated = successCount + alreadyUpdatedCount;
      if (totalUpdated > 0) {
        let message = `Successfully updated ${successCount} out of ${selectedEmails.length} users`;
        if (alreadyUpdatedCount > 0) {
          message += ` (${alreadyUpdatedCount} were already updated in Saviynt)`;
        }
        toast.success(message);

        // Refresh the data
        await queryClient.invalidateQueries({
          queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
        });

        await queryClient.refetchQueries({
          queryKey: ['updateTraningPage', 'updateTranningPageData', locale],
          type: 'active',
        });

        setSelectedIds([]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in bulk update:', errorMessage);
      toast.error(`Error during bulk update: ${errorMessage}`);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (email) {
      handleUpdateStatus(email);
    } else {
      toast.error('Please enter email');
    }
  };

  const headers = [
    { label: 'Id', key: 'id' },
    { label: 'Email', key: 'email' },
    { label: 'UPN Email', key: 'upn_email' },
    { label: 'User Name', key: 'username' },
    { label: 'Training Status', key: 'training_status' },
    { label: 'Training Start Date', key: 'training_start_date' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      filterable: false,
      width: 60,
      renderCell: (params) => params.value,
    },
    { field: 'email', headerName: 'Email', width: 280 },
    { field: 'upn_email', headerName: 'UPN Email', width: 280 },
    { field: 'username', headerName: 'User Name', width: 250 },
    {
      field: 'training_status',
      headerName: 'Training Status',
      width: 150,
      renderCell: (params) => (params.value ? params.value : 'Not Completed'),
    },
    { field: 'training_start_date', headerName: 'Training Start Date', width: 200 },
    {
      field: 'update_status',
      headerName: 'Update Status',
      width: 150,
      renderCell: (params) => (
        <Button variant="contained" color="primary" onClick={() => handleUpdateStatus(params.row.email)}>
          Update Status
        </Button>
      ),
    },
  ];

  interface TrainingData {
    id: number;
    email: string;
    username: string;
    zone: string;
    training_status: string | null;
    training_start_date: string;
    training_end_date: string | null;
  }

  interface TrainingDataResponse {
    success: boolean;
    data: TrainingData[];
  }

  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<TrainingDataResponse>(
    ['updateTraningPage', 'updateTranningPageData', locale],
    {
      url: `${fixed}admin/${locale || localeFromStorage}/cyber_training_data`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    {
      refetchOnWindowFocus: true,
      enabled: true,
    },
  );

  if (isPending) return <AdminPageLoader />;
  if (error) return <div>Error: {error.message}</div>;
  if (data && !data.success) return <AdminInternalError />;

  const preprocessDataForCSV = (data: TrainingData[]) => {
    return data.map((item) => ({
      ...item,
      training_status: item.training_status ? item.training_status : 'Not Completed',
    }));
  };

  const filteredData = Array.isArray(data?.data)
    ? data.data.filter((item: TrainingData) => {
        return !item.training_status || !item.training_status.toLowerCase().includes('completed');
      })
    : [];

  const csvData = preprocessDataForCSV(filteredData);

  return (
    <AdminWrapper>
      <AdminPageHeader
        title="Update training status to completed"
        locale={locale}
        setLocale={setLocale}
        showLocaleButtonGroup={false}
      />

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3} alignItems={'center'}>
          <Grid item md={6}>
            <Box className="form-group">
              <InputLabel id="email-label">Enter email to update the training Status</InputLabel>
              <TextField
                placeholder="Enter User Email"
                size="small"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Box>
          </Grid>
          <Grid item md={6}>
            <Button type="submit" variant="contained" color="primary">
              Update Status
            </Button>
            <Button
              type="button"
              variant="contained"
              color="secondary"
              disabled={selectedIds.length < 2}
              onClick={handleBulkUpdate}
              sx={{ mx: 2 }}
            >
              Bulk Update ({selectedIds.length})
            </Button>
            <Button
              type="button"
              sx={{ color: '#fff', marginLeft: '10px' }}
              variant="contained"
              size="small"
              color="success"
              className="download-csv-button"
              title="Download CSV"
            >
              <CSVLink data={csvData} headers={headers}>
                <CloudDownloadIcon sx={{ fontSize: '18px' }} /> CSV
              </CSVLink>
            </Button>
          </Grid>
        </Grid>
      </Box>

      <AdminDataGrid<TrainingData>
        data={{ data: filteredData }}
        columns={columns}
        setSelectedIds={setSelectedIds}
        showCheckbox={true}
      />
    </AdminWrapper>
  );
};

export default UpdateTrainingStatus;
